import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { toast } from '@CommonComponents/toast'
import { client } from '@Utils'
import { getErrorMessage } from '../../utils'
import { ConfirmModal } from '../modals/ConfirmModal'

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    marginBottom: '8px',
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease',
  },
  cardHover: {
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  collapsedRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    cursor: 'pointer',
    backgroundColor: '#ffffff',
    transition: 'background-color 0.15s ease',
  },
  collapsedRowUnresolved: {
    borderLeft: '4px solid #dc3545',
  },
  collapsedRowResolved: {
    borderLeft: '4px solid #28a745',
  },
  indexBadge: {
    backgroundColor: '#6c757d',
    color: '#ffffff',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginRight: '12px',
    minWidth: '32px',
    textAlign: 'center',
  },
  collapsedContent: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem',
    color: '#495057',
    flexWrap: 'wrap',
  },
  collapsedEmail: {
    fontWeight: 600,
    color: '#004876',
  },
  separator: {
    color: '#dee2e6',
    margin: '0 4px',
  },
  statusBadge: {
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginLeft: 'auto',
  },
  resolvedBadge: {
    backgroundColor: '#d4edda',
    color: '#155724',
  },
  unresolvedBadge: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
  chevron: {
    marginLeft: '12px',
    color: '#6c757d',
    transition: 'transform 0.2s ease',
  },
  expandedContainer: {
    borderTop: '1px solid #e9ecef',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e9ecef',
  },
  headerTitle: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 600,
    color: '#343a40',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    padding: '4px 8px',
    cursor: 'pointer',
    color: '#6c757d',
    fontSize: '1.25rem',
    borderRadius: '4px',
    transition: 'background-color 0.15s ease',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#ffffff',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  summaryLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  summaryValue: {
    fontSize: '0.95rem',
    color: '#212529',
    wordBreak: 'break-word',
  },
  actionBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #e9ecef',
    borderBottom: '1px solid #e9ecef',
    flexWrap: 'wrap',
  },
  detailsSection: {
    padding: '16px',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '16px',
  },
  detailsPanel: {
    backgroundColor: '#f8f9fa',
    borderRadius: '6px',
    padding: '14px',
    border: '1px solid #e9ecef',
  },
  panelTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#004876',
    marginBottom: '10px',
    paddingBottom: '8px',
    borderBottom: '1px solid #dee2e6',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  panelRow: {
    fontSize: '0.85rem',
    marginBottom: '6px',
    display: 'flex',
  },
  panelLabel: {
    fontWeight: 600,
    color: '#495057',
    minWidth: '100px',
    flexShrink: 0,
  },
  panelValue: {
    color: '#212529',
    wordBreak: 'break-all',
  },
  diagnosticPanel: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
  },
  diagnosticTitle: {
    color: '#856404',
  },
}

export const GradeLogViewer = ({ index, gradeLog, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false)

  const handleOpenState = () => {
    setIsOpen(!isOpen)
  }

  const showSuccess = message => {
    toast.success(message)
  }

  const showError = message => {
    toast.error(message)
  }

  const getGradeId = () => {
    return gradeLog.grade && gradeLog.grade.id
  }

  const hasSubmission = gradeLog.submission && gradeLog.submission.id
  // ActivityGrade.points is what actually gets sent to the LMS
  const hasGrade = gradeLog.grade && gradeLog.grade.id
  const hasResourceLink = gradeLog.resourceLink && gradeLog.resourceLink.id
  // ResourceLink.context is SET_NULL, so a reslink can be orphaned from its
  // context; lookups are context-scoped, so offer to re-attach the GradeLog's
  const resourceLinkNeedsContext =
    hasResourceLink &&
    !gradeLog.resourceLink.contextId &&
    gradeLog.context &&
    gradeLog.context.id

  const handleRetry = async () => {
    setIsLoading(true)
    try {
      await client.post(`/account/admin_lti13.retryGradeLog/-/${gradeLog.id}/`)
      showSuccess('Grade successfully pushed to LMS')
      onRefresh()
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to retry grade push')
      showError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    setIsLoading(true)
    try {
      await client.delete(
        `/account/admin_lti13.deleteGradeLog/-/${gradeLog.id}/`
      )
      showSuccess('Grade log deleted')
      onRefresh()
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to delete grade log')
      showError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRepairLineItem = async () => {
    setIsLoading(true)
    try {
      const resp = await client.post(
        `/account/admin_lti13.repairGradeLogLineItem/-/${gradeLog.id}/`
      )
      showSuccess(resp.data.message || 'Line item URL restored')
      if (resp.data.warning) {
        toast.warn(resp.data.warning)
      }
      onRefresh()
    } catch (err) {
      const errorMsg = getErrorMessage(
        err,
        'Failed to repair the line item binding'
      )
      showError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAttachContext = async () => {
    setIsLoading(true)
    try {
      const resp = await client.post(
        `/account/admin_lti13.attachResourceLinkContext/-/${gradeLog.id}/`
      )
      showSuccess(resp.data.message || 'Context attached to the resource link')
      onRefresh()
    } catch (err) {
      const errorMsg = getErrorMessage(
        err,
        'Failed to attach the context to the resource link'
      )
      showError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAllForGrade = async () => {
    var gradeId = getGradeId()
    if (!gradeId) {
      showError('No grade associated with this log')
      return
    }
    setIsLoading(true)
    try {
      const resp = await client.delete(
        `/account/admin_lti13.deleteGradeLogsForGrade/-/${gradeId}/`
      )
      showSuccess(`Deleted ${resp.data.deletedCount} grade log(s)`)
      onRefresh()
    } catch (err) {
      const errorMsg = getErrorMessage(err, 'Failed to delete grade logs')
      showError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const cardStyle = {
    ...styles.card,
    ...(isHovered && !isOpen ? styles.cardHover : {}),
  }

  const collapsedStyle = {
    ...styles.collapsedRow,
    ...(gradeLog.resolved
      ? styles.collapsedRowResolved
      : styles.collapsedRowUnresolved),
  }

  const statusBadgeStyle = {
    ...styles.statusBadge,
    ...(gradeLog.resolved ? styles.resolvedBadge : styles.unresolvedBadge),
  }

  return (
    <div
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ConfirmModal
        show={showDeleteModal}
        toggle={setShowDeleteModal}
        title="Delete Grade Log"
        message="Are you sure you want to delete this grade log?"
        onConfirm={handleDelete}
        confirmText="Delete"
      />
      <ConfirmModal
        show={showDeleteAllModal}
        toggle={setShowDeleteAllModal}
        title="Delete All Grade Logs"
        message="Are you sure you want to delete ALL grade logs for this grade? This will remove all retry attempts."
        onConfirm={handleDeleteAllForGrade}
        confirmText="Delete All"
      />

      {/* Collapsed View */}
      <div style={collapsedStyle} onClick={handleOpenState}>
        <span style={styles.indexBadge}>{index}</span>
        <div style={styles.collapsedContent}>
          <span style={styles.collapsedEmail}>{gradeLog.user.email}</span>
          <span style={styles.separator}>|</span>
          <span>{gradeLog.course.coursenum}</span>
          <span style={styles.separator}>|</span>
          <span>{gradeLog.activity.title}</span>
          <span style={styles.separator}>|</span>
          <span>
            {hasGrade
              ? `${gradeLog.grade.points}/${gradeLog.activity.points} pts`
              : 'no grade'}
          </span>
        </div>
        <span style={statusBadgeStyle}>
          {gradeLog.resolved ? 'Resolved' : 'Unresolved'}
        </span>
        <i
          className={`fa-regular fa-chevron-${isOpen ? 'up' : 'down'}`}
          style={styles.chevron}
        />
      </div>

      {/* Expanded View */}
      {isOpen && (
        <div style={styles.expandedContainer}>
          {/* Header */}
          <div style={styles.header}>
            <h4 style={styles.headerTitle}>
              <i className="fa-regular fa-file-alt" />
              Grade Log Details
            </h4>
            <button
              style={styles.closeButton}
              onClick={handleOpenState}
              title="Close"
            >
              <i className="fa-regular fa-times" />
            </button>
          </div>

          {/* Summary Grid */}
          <div style={styles.summaryGrid}>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Student Email</span>
              <span style={styles.summaryValue}>
                {gradeLog.user.dashboardLink ? (
                  <a
                    href={gradeLog.user.dashboardLink}
                    target="_blank"
                    rel="noreferrer"
                    title="Open the student's support dashboard"
                  >
                    {gradeLog.user.email}
                  </a>
                ) : (
                  gradeLog.user.email
                )}
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Student Name</span>
              <span style={styles.summaryValue}>{gradeLog.user.fullName}</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Course</span>
              <span style={styles.summaryValue}>
                <a
                  href={`/reader/web/${gradeLog.course.coursenum}/`}
                  target="_blank"
                  rel="noreferrer"
                  title="Open the course title page"
                >
                  {gradeLog.course.coursenum} - {gradeLog.course.title}
                </a>
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Activity</span>
              <span style={styles.summaryValue}>
                <a
                  href={`/instructor/activity_submissions/${gradeLog.course.coursenum}/${gradeLog.activity.id}/`}
                  target="_blank"
                  rel="noreferrer"
                  title="Open the activity submissions page"
                >
                  {gradeLog.activity.title}
                </a>
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Grade (Sent to LMS)</span>
              <span style={styles.summaryValue}>
                {hasGrade
                  ? `${gradeLog.grade.points} / ${gradeLog.activity.points}`
                  : 'no grade'}
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Date</span>
              <span style={styles.summaryValue}>{gradeLog.createDate}</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Grade Log ID</span>
              <span style={styles.summaryValue}>{gradeLog.id}</span>
            </div>
          </div>

          {/* Action Bar */}
          <div style={styles.actionBar}>
            <Button
              variant="primary"
              size="sm"
              onClick={handleRetry}
              disabled={isLoading || gradeLog.resolved}
            >
              <i className="fa-regular fa-redo mr-1" />
              {isLoading ? 'Processing...' : 'Retry Grade Push'}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setShowDeleteModal(true)}
              disabled={isLoading}
            >
              <i className="fa-regular fa-trash mr-1" />
              Delete Log
            </Button>
            <Button
              variant="warning"
              size="sm"
              onClick={() => setShowDeleteAllModal(true)}
              disabled={isLoading || !getGradeId()}
            >
              <i className="fa-regular fa-trash-alt mr-1" />
              Delete All for Grade
            </Button>
          </div>

          {/* Details Panels */}
          <div style={styles.detailsSection}>
            <div style={styles.detailsGrid}>
              {/* Context Panel */}
              <div style={styles.detailsPanel}>
                <div style={styles.panelTitle}>
                  <i className="fa-regular fa-sitemap" />
                  Context Information
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>ID:</span>
                  <span style={styles.panelValue}>{gradeLog.context.id}</span>
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>Context ID:</span>
                  <span style={styles.panelValue}>
                    {gradeLog.context.contextId}
                  </span>
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>Title:</span>
                  <span style={styles.panelValue}>
                    {gradeLog.context.contextTitle}
                  </span>
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>Label:</span>
                  <span style={styles.panelValue}>
                    {gradeLog.context.contextLabel}
                  </span>
                </div>
              </div>

              {/* Resource Link Panel */}
              <div style={styles.detailsPanel}>
                <div style={styles.panelTitle}>
                  <i className="fa-regular fa-link" />
                  Resource Link
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>ID:</span>
                  <span style={styles.panelValue}>
                    {gradeLog.resourceLink && gradeLog.resourceLink.id}
                  </span>
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>GUID:</span>
                  <span style={styles.panelValue}>
                    {gradeLog.resourceLink && gradeLog.resourceLink.guid}
                  </span>
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>Line Item URL:</span>
                  <span style={styles.panelValue}>
                    {(gradeLog.resourceLink &&
                      gradeLog.resourceLink.lineItemUrl) ||
                      'Not set'}
                  </span>
                </div>
                {hasResourceLink && (
                  <div style={styles.panelRow}>
                    <span style={styles.panelLabel}>Context:</span>
                    <span style={styles.panelValue}>
                      {gradeLog.resourceLink.contextId
                        ? `Attached (ID: ${gradeLog.resourceLink.contextId})`
                        : 'Not attached'}
                    </span>
                  </div>
                )}
                {resourceLinkNeedsContext && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{ marginTop: '8px', marginRight: '8px' }}
                    onClick={handleAttachContext}
                    disabled={isLoading}
                    title="Attach this grade log's context to the resource link so context-scoped lookups and repairs can find it"
                  >
                    <i className="fa-regular fa-link mr-1" />
                    {isLoading ? 'Processing...' : 'Attach Context'}
                  </Button>
                )}
                {!(
                  gradeLog.resourceLink && gradeLog.resourceLink.lineItemUrl
                ) && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{ marginTop: '8px' }}
                    onClick={handleRepairLineItem}
                    disabled={isLoading}
                    title="Look up the line item in the LMS (by resourceId, falling back to the activity title) and save its URL on the resource link, reattaching the activity's existing resource link if this log lost it. Restores a missing resourceId in the LMS."
                  >
                    <i className="fa-regular fa-wrench mr-1" />
                    {isLoading ? 'Processing...' : 'Find & Fix Line Item'}
                  </Button>
                )}
              </div>

              {/* Submission Panel */}
              {hasSubmission && (
                <div style={styles.detailsPanel}>
                  <div style={styles.panelTitle}>
                    <i className="fa-regular fa-clipboard-check" />
                    Submission
                  </div>
                  <div style={styles.panelRow}>
                    <span style={styles.panelLabel}>ID:</span>
                    <span style={styles.panelValue}>
                      {gradeLog.submission.id}
                    </span>
                  </div>
                  <div style={styles.panelRow}>
                    <span style={styles.panelLabel}>Points:</span>
                    <span style={styles.panelValue}>
                      {gradeLog.submission.points}
                    </span>
                  </div>
                  <div style={styles.panelRow}>
                    <span style={styles.panelLabel}>Started:</span>
                    <span style={styles.panelValue}>
                      {gradeLog.submission.startTime}
                    </span>
                  </div>
                  <div style={styles.panelRow}>
                    <span style={styles.panelLabel}>Submitted:</span>
                    <span style={styles.panelValue}>
                      {gradeLog.submission.endTime}
                    </span>
                  </div>
                  <div style={styles.panelRow}>
                    <span style={styles.panelLabel}>Graded:</span>
                    <span style={styles.panelValue}>
                      {gradeLog.submission.gradeTime}
                    </span>
                  </div>
                </div>
              )}

              {/* Activity Grade Panel */}
              {hasGrade && (
                <div style={styles.detailsPanel}>
                  <div style={styles.panelTitle}>
                    <i className="fa-regular fa-graduation-cap" />
                    Activity Grade
                  </div>
                  <div style={styles.panelRow}>
                    <span style={styles.panelLabel}>ID:</span>
                    <span style={styles.panelValue}>{gradeLog.grade.id}</span>
                  </div>
                  <div style={styles.panelRow}>
                    <span style={styles.panelLabel}>Points:</span>
                    <span style={styles.panelValue}>
                      {gradeLog.grade.points} / {gradeLog.activity.points}
                    </span>
                  </div>
                </div>
              )}

              {/* Diagnostic Panel */}
              <div
                style={{ ...styles.detailsPanel, ...styles.diagnosticPanel }}
              >
                <div
                  style={{ ...styles.panelTitle, ...styles.diagnosticTitle }}
                >
                  <i className="fa-regular fa-exclamation-circle" />
                  Diagnostic Information
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>Status Code:</span>
                  <span style={styles.panelValue}>{gradeLog.statusCode}</span>
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>Info:</span>
                  <span style={styles.panelValue}>
                    {gradeLog.info || 'N/A'}
                  </span>
                </div>
                <div style={styles.panelRow}>
                  <span style={styles.panelLabel}>Response:</span>
                  <span style={styles.panelValue}>
                    {gradeLog.respText || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
